import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Footer from '../components/Footer';
import ImageGenerator from '../components/ImageGenerator';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

interface ScheduledPost {
  day: number;
  date: string;
  platform: string;
  type: string;
  hook: string;
  content: string;
  hashtags: string[];
}

interface CalendarData {
  posts: ScheduledPost[];
  courseName: string;
}

const SocialCalendar: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { token, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedWeek, setSelectedWeek] = useState(0);

  useEffect(() => {
    fetchCourse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundCourse = response.data.courses.find((c: any) => c.id === courseId);
      if (foundCourse) {
        setCourse(foundCourse);
      } else {
        toast.error('Course not found');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const generateCalendar = async () => {
    if (!course) return;

    if (user?.tier === 'free') {
      toast.error('Please upgrade to Basic or Premium to generate a social calendar');
      navigate('/pricing');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/ai/generate-social-calendar`,
        {
          courseTitle: course.title,
          courseDescription: course.description,
          targetAudience: course.targetAudience || '',
          niche: course.niche || ''
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setCalendarData(response.data.calendar);
      toast.success('30-day social calendar generated!');
    } catch (error: any) {
      console.error('Error generating calendar:', error);
      toast.error(error.response?.data?.error || 'Failed to generate calendar');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getPlatformColor = (platform: string) => {
    const colors: Record<string, string> = {
      'Instagram': 'bg-gradient-to-r from-purple-500 to-pink-500',
      'Twitter/X': 'bg-black',
      'LinkedIn': 'bg-blue-700',
      'Facebook': 'bg-blue-600',
      'TikTok': 'bg-gradient-to-r from-cyan-500 to-pink-500'
    };
    return colors[platform] || 'bg-gray-600';
  };

  const getPlatformIcon = (platform: string) => {
    const icons: Record<string, string> = {
      'Instagram': '📸',
      'Twitter/X': '𝕏',
      'LinkedIn': '💼',
      'Facebook': '👥',
      'TikTok': '🎵'
    };
    return icons[platform] || '📱';
  };

  const getWeekPosts = (weekNum: number) => {
    if (!calendarData) return [];
    const startDay = weekNum * 7 + 1;
    const endDay = startDay + 6;
    return calendarData.posts.filter(p => p.day >= startDay && p.day <= endDay);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/marketing/${courseId}`)}
                className="text-gray-600 hover:text-gray-900"
              >
                ← Back to Marketing
              </button>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  The Course Creator's Vault
                </h1>
                <p className="text-xs text-gray-500">by Templimize AI</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">{user?.name}</div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-4xl font-bold mb-4">📅 30-Day Social Media Calendar</h2>
          <p className="text-indigo-100 text-lg mb-2">
            For: <strong>{course?.title}</strong>
          </p>
          <p className="text-indigo-200">
            AI-generated social media posts for Instagram, Twitter/X, LinkedIn, Facebook, and TikTok - spread across 30 days for maximum engagement!
          </p>
        </div>

        {/* Generate Button */}
        {!calendarData && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200 mb-8">
            <div className="max-w-2xl mx-auto">
              <div className="text-6xl mb-6">📆</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Ready to Plan Your Launch?
              </h3>
              <p className="text-gray-600 mb-8 text-lg">
                Our AI will generate a complete 30-day social media calendar including:
              </p>
              <div className="grid grid-cols-5 gap-4 mb-8">
                {['Instagram', 'Twitter/X', 'LinkedIn', 'Facebook', 'TikTok'].map(platform => (
                  <div key={platform} className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl mb-2">{getPlatformIcon(platform)}</div>
                    <div className="text-sm font-medium text-gray-700">{platform}</div>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 mb-6">
                Mix of promotional, educational, engagement, and behind-the-scenes content
              </p>
              <button
                onClick={generateCalendar}
                disabled={generating}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-12 py-4 rounded-full text-xl font-bold hover:shadow-2xl transform hover:scale-105 transition disabled:opacity-50"
              >
                {generating ? (
                  <span className="flex items-center justify-center">
                    <span className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin mr-3"></span>
                    Generating 30 Days of Content...
                  </span>
                ) : (
                  '✨ Generate 30-Day Calendar'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Calendar Display */}
        {calendarData && (
          <>
            {/* View Toggle & Stats */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8">
              <div className="flex flex-wrap justify-between items-center gap-4">
                <div className="flex gap-4">
                  <div className="bg-purple-100 px-4 py-2 rounded-lg">
                    <span className="text-purple-600 font-bold">{calendarData.posts.length}</span>
                    <span className="text-purple-600 ml-1">Total Posts</span>
                  </div>
                  <div className="bg-green-100 px-4 py-2 rounded-lg">
                    <span className="text-green-600 font-bold">30</span>
                    <span className="text-green-600 ml-1">Days Covered</span>
                  </div>
                  <div className="bg-blue-100 px-4 py-2 rounded-lg">
                    <span className="text-blue-600 font-bold">5</span>
                    <span className="text-blue-600 ml-1">Platforms</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      viewMode === 'calendar'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    📅 Calendar
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      viewMode === 'list'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    📋 List
                  </button>
                </div>
              </div>
            </div>

            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <div className="space-y-6">
                {/* Week Navigation */}
                <div className="flex justify-center gap-2">
                  {[0, 1, 2, 3, 4].map(week => (
                    <button
                      key={week}
                      onClick={() => setSelectedWeek(week)}
                      className={`px-4 py-2 rounded-lg font-medium transition ${
                        selectedWeek === week
                          ? 'bg-purple-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-purple-50 border border-gray-200'
                      }`}
                    >
                      Week {week + 1}
                    </button>
                  ))}
                </div>

                {/* Week Grid */}
                <div className="grid grid-cols-7 gap-4">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <div key={day} className="text-center font-semibold text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                  {Array.from({ length: 7 }, (_, i) => {
                    const dayNum = selectedWeek * 7 + i + 1;
                    const dayPosts = calendarData.posts.filter(p => p.day === dayNum);
                    return (
                      <div
                        key={i}
                        className={`bg-white rounded-xl shadow border border-gray-200 p-4 min-h-[150px] ${
                          dayNum > 30 ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="text-lg font-bold text-gray-900 mb-2">Day {dayNum}</div>
                        {dayPosts.map((post, idx) => (
                          <div
                            key={idx}
                            className={`${getPlatformColor(post.platform)} text-white text-xs px-2 py-1 rounded mb-1 cursor-pointer hover:opacity-90`}
                            onClick={() => copyToClipboard(`${post.hook}\n\n${post.content}\n\n${post.hashtags.join(' ')}`)}
                            title="Click to copy"
                          >
                            {getPlatformIcon(post.platform)} {post.type}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="space-y-4">
                {calendarData.posts.map((post, idx) => (
                  <div key={idx} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    <div className={`${getPlatformColor(post.platform)} text-white px-6 py-3 flex justify-between items-center`}>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getPlatformIcon(post.platform)}</span>
                        <div>
                          <div className="font-bold">{post.platform}</div>
                          <div className="text-sm opacity-80">Day {post.day} - {post.type}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => copyToClipboard(`${post.hook}\n\n${post.content}\n\n${post.hashtags.join(' ')}`)}
                        className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg font-medium transition"
                      >
                        📋 Copy All
                      </button>
                    </div>
                    <div className="p-6 space-y-4">
                      <div>
                        <div className="text-sm font-semibold text-gray-500 mb-1">HOOK</div>
                        <p className="text-lg font-bold text-gray-900">{post.hook}</p>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-500 mb-1">CONTENT</div>
                        <p className="text-gray-700 whitespace-pre-wrap">{post.content}</p>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-500 mb-1">HASHTAGS</div>
                        <div className="flex flex-wrap gap-2">
                          {post.hashtags.map((tag, tagIdx) => (
                            <span
                              key={tagIdx}
                              className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm cursor-pointer hover:bg-gray-200"
                              onClick={() => copyToClipboard(tag)}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Generate Images for Posts */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden mt-8">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
                <h3 className="text-2xl font-bold">🎨 Generate Images for Your Posts</h3>
                <p className="text-purple-100">Create matching visuals for your social media content</p>
              </div>
              <div className="p-6">
                <ImageGenerator
                  courseTitle={course?.title || 'Course'}
                  defaultPrompt={`Eye-catching social media graphic for "${course?.title}" course. Professional, engaging, and designed to stop the scroll.`}
                  platform="instagram"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mt-8">
              <div className="flex flex-wrap gap-4 justify-center">
                <button
                  onClick={generateCalendar}
                  disabled={generating}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-lg font-bold hover:shadow-xl transition disabled:opacity-50"
                >
                  🔄 Regenerate Calendar
                </button>
                <button
                  onClick={() => {
                    const allContent = calendarData.posts.map(p =>
                      `Day ${p.day} - ${p.platform} (${p.type})\n\nHook: ${p.hook}\n\n${p.content}\n\nHashtags: ${p.hashtags.join(' ')}\n\n---\n`
                    ).join('\n');
                    copyToClipboard(allContent);
                  }}
                  className="border-2 border-purple-600 text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-purple-50 transition"
                >
                  📋 Copy All Posts
                </button>
                <button
                  onClick={() => navigate(`/marketing/${courseId}`)}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:border-gray-400 transition"
                >
                  ← Back to Marketing
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SocialCalendar;
